import { useAuthFetch } from "@p0security/frontend-core";
import {
  DEFAULT_MAX_ACCESS_DURATION,
  DEFAULT_STANDING_ACCESS_DURATION,
} from "@p0security/shared/configuration/constant";
import {
  sortApprovalOptions,
  timeUnitLabelOptions,
  timeUnitLabels,
} from "@p0security/shared/permission-requests/util";
import { printUserFriendlyDuration } from "@p0security/shared/permission-requests/util";
import {
  Alert,
  Col,
  Divider,
  InputNumber,
  Row,
  Select,
  Skeleton,
  Space,
  Spacing,
  Typography,
  message,
} from "@p0security/ui";
import { AuthzButton } from "components/common/AuthzButton";
import { useCallback, useEffect, useMemo, useState } from "react";
import { handleErrorWithToastMessage } from "utils/error";

import { useDurationSettings } from "./useDurationSettings";

const style: React.CSSProperties = { padding: "8px 0", flex: "1  1  0px" };

export const StandingAccessDuration: React.FC = () => {
  const [time, setTime] = useState<number | null>(1);
  const [unit, setUnit] = useState<keyof typeof timeUnitLabels>("m");
  const [saving, setSaving] = useState(false);

  const optionsDoc = useDurationSettings();

  const maxAccessDuration = useMemo(() => {
    return (
      optionsDoc.doc?.data.maxAccessDuration || DEFAULT_MAX_ACCESS_DURATION
    );
  }, [optionsDoc.doc]);

  const standingAccessDuration = useMemo(() => {
    return (
      optionsDoc.doc?.data.maxStandingAccessDuration ||
      DEFAULT_STANDING_ACCESS_DURATION
    );
  }, [optionsDoc.doc]);

  useEffect(() => {
    setTime(standingAccessDuration.time);
    setUnit(standingAccessDuration.unit);
  }, [standingAccessDuration]);

  const standingAccessExceedsMaxAccess = useMemo(() => {
    return sortApprovalOptions(standingAccessDuration, maxAccessDuration) > 0;
  }, [maxAccessDuration, standingAccessDuration]);

  const authFetch = useAuthFetch(handleErrorWithToastMessage);

  const setStandingAccessDuration = useCallback(async () => {
    if (time && unit) {
      setSaving(true);
      const response = await authFetch(`settings/standing-access-duration`, {
        json: { time, unit },
        method: "PUT",
      });
      setSaving(false);
      if (!response) {
        setTime(standingAccessDuration.time);
        setUnit(standingAccessDuration.unit);
        return;
      }
      message.success("Standing access duration set");
    }
  }, [time, unit, authFetch, standingAccessDuration]);

  return (
    <div>
      <Typography.Title level={4}>
        Organization maximum standing access duration
      </Typography.Title>
      <div>
        <Typography.Text type="secondary">
          Set the default and organization maximum standing access duration for
          always-allow and auto-approve (on-call) access requests.
        </Typography.Text>
        {standingAccessExceedsMaxAccess && (
          <Spacing asChild mt={8}>
            <Alert
              message="Standing access duration exceeds the maximum access duration"
              type="warning"
              showIcon
              description={`Persistent access requests will automatically default to the minimum allowable duration of ${printUserFriendlyDuration(
                maxAccessDuration
              )}.`}
            />
          </Spacing>
        )}
        <br />
        {optionsDoc.loading ? (
          <Skeleton.Input active block={true} />
        ) : (
          <Row gutter={{ xs: 8, sm: 16, md: 24, lg: 32 }}>
            <Col className="gutter-row" span={24}>
              <div style={style}>
                <Typography.Text>
                  Maximum standing access duration:{" "}
                </Typography.Text>
                <Space>
                  <Space.Compact>
                    <InputNumber min={1} value={time} onChange={setTime} />
                    <Select
                      options={timeUnitLabelOptions}
                      value={unit}
                      onChange={setUnit}
                      /* eslint-disable-line react/forbid-component-props */ style={{
                        width: 97,
                      }}
                    />
                  </Space.Compact>
                  <Divider />
                  <AuthzButton
                    roles={["owner"]}
                    type="primary"
                    loading={saving}
                    onClick={setStandingAccessDuration}
                  >
                    Set
                  </AuthzButton>
                </Space>
              </div>
            </Col>
          </Row>
        )}
      </div>
    </div>
  );
};
